import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { LOCAL_STORAGE_KEYS } from "@swiggy-private/core-ui";
import {
    useLocalStorageBasedFrequencyCapping,
    SessionVisibility,
    CartOverlaysContext,
    ProceedMode,
} from "@swiggy-private/checkout-core";
import { ICrossSellHeader, GlobalOverlayPriority, LottieIds, OverlayType } from "@swiggy-private/cart-types";
import { MAGIC_ITEMS_TYPE } from "../constants";

export interface UseMagicItemsProps {
    presentationInfo: ICrossSellHeader["presentationInfo"];
    sid?: string;
    onTimerExpired?: VoidFunction;
    onImpressionMagicItemsFtue?: VoidFunction;
    onImpressionMagicItemsTimer?: (nearExpiry: boolean) => void;
    onImpressionMagicItemsEmpty?: (savingsText?: string) => void;
    allCrossSellSuggestionsAdded?: boolean;
    allCrossSellSuggestionsAddedText?: string;
}

export interface UseMagicItemsReturn {
    // Magic items state
    isMagicItems: boolean;
    isWidgetUnlocked: boolean;
    isMagicItemsExpired: boolean;
    showMagicItemsUI: boolean;
    showOnlyMessageAndTimer: boolean;

    // Lottie overlay state
    showLottieOverlay: boolean;
    isLottieVisible: boolean;
    lottieUrl: string | undefined;
    handleMagicDealsLottieComplete: () => void;
    handleLottieDOMLoaded: () => void;
    shouldPlayLottie: boolean; // True when magic items lottie is the active overlay (no higher priority overlays)

    // Timer handlers
    handleTimerComplete: () => void;
    handleTimerStart: () => void;
    handleUrgencyTimeReached: () => void;
}

export const useMagicItems = ({
    presentationInfo,
    sid,
    onTimerExpired,
    onImpressionMagicItemsFtue,
    onImpressionMagicItemsTimer,
    onImpressionMagicItemsEmpty,
    allCrossSellSuggestionsAdded = false,
    allCrossSellSuggestionsAddedText,
}: UseMagicItemsProps): UseMagicItemsReturn => {
    // Get overlay context for priority management
    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);
    const isLottieRegisteredRef = useRef(false);

    // Compute maxFrequency from presentationInfo
    const maxFrequency = useMemo(() => {
        return presentationInfo?.ftue_animation?.threshold_limit;
    }, [presentationInfo]);

    // Hook for frequency capping - once per session, uses threshold_limit from ftue_animation
    const { shouldProceed } = useLocalStorageBasedFrequencyCapping({
        key: LOCAL_STORAGE_KEYS.CROSS_SELL_MAGIC_DEALS_OVERLAY_LOTTIE,
        maxFrequency, // Dynamic threshold from ftue_animation.threshold_limit
        sessionVisibility: SessionVisibility.ONCE_PER_SESSION, // Once per session
        sessionId: sid,
    });

    // Lottie animation URL from backend
    const lottieUrl = useMemo(() => {
        return presentationInfo?.ftue_animation?.lottie;
    }, [presentationInfo]);

    // State to track if lottie has completed (to hide overlay after completion)
    const [hasLottieCompleted, setHasLottieCompleted] = useState(false);
    // State to track if lottie is loaded/visible (to hide white overlay)
    const [isLottieVisible, setIsLottieVisible] = useState(false);

    // Handler for lottie completion
    const handleMagicDealsLottieComplete = useCallback(() => {
        setHasLottieCompleted(true);
        // Close the overlay when lottie completes so next priority overlay can be shown
        if (enabledOverlayId === LottieIds.MAGIC_ITEMS_LOTTIE) {
            onCloseOverlay();
        }
    }, [enabledOverlayId, onCloseOverlay]);

    // Handler for when lottie DOM is loaded and visible
    const handleLottieDOMLoaded = useCallback(() => {
        setIsLottieVisible(true);
    }, []);

    // Check if this is Magic Items (NEW)
    const isMagicItems = useMemo(() => {
        return presentationInfo?.widget_properties_info?.type === MAGIC_ITEMS_TYPE;
    }, [presentationInfo]);

    const isWidgetUnlocked = useMemo(() => {
        return presentationInfo?.widget_properties_info?.properties?.is_widget_unlocked ?? false;
    }, [presentationInfo]);

    // Derive timer expiration from backend response - timer is expired when remaining_time is 0
    // This keeps magic items visible until API response confirms expiration
    const isMagicItemsExpired = useMemo(() => {
        const remainingTime = presentationInfo?.timer_data?.remaining_time?.time_in_sec;
        // Only consider expired if remaining_time is explicitly 0
        // undefined means no timer data yet (initial load) - not expired
        return remainingTime === 0;
    }, [presentationInfo?.timer_data?.remaining_time?.time_in_sec]);

    // Handler for timer completion - triggers API call to confirm expiration
    const handleTimerComplete = useCallback(() => {
        // Call onTimerExpired to trigger reloadCart
        // Magic items will stay visible until API response with remaining_time = 0
        onTimerExpired?.();
    }, [onTimerExpired]);

    // Calculate effective magic items state (magic items AND not expired)
    const showMagicItemsUI = useMemo(() => {
        return isMagicItems && !isMagicItemsExpired;
    }, [isMagicItems, isMagicItemsExpired]);

    // Calculate if we should show the lottie overlay synchronously to prevent flash
    // This is computed during render, not in useEffect, so carousel can be hidden immediately
    const showLottieOverlay = useMemo(() => {
        // Only show overlay for Magic Items and if lottie hasn't completed
        if (!showMagicItemsUI || hasLottieCompleted) {
            return false;
        }

        //check frequency capping during render update it when the lottie actually plays
        return shouldProceed({ mode: ProceedMode.ONLY_CHECK });
    }, [shouldProceed, showMagicItemsUI, hasLottieCompleted]);

    // Register overlay with priority system when lottie should be shown
    useEffect(() => {
        if (showLottieOverlay && !isLottieRegisteredRef.current) {
            registerOverlays({
                overlayType: OverlayType.LOTTIE,
                priority: GlobalOverlayPriority.LOTTIE_ANIMATION,
                id: LottieIds.MAGIC_ITEMS_LOTTIE,
            });
            isLottieRegisteredRef.current = true;
        }
    }, [showLottieOverlay, registerOverlays]);

    // Check if this lottie is the active overlay (no higher priority overlays are visible)
    const shouldPlayLottie = useMemo(() => {
        return enabledOverlayId === LottieIds.MAGIC_ITEMS_LOTTIE;
    }, [enabledOverlayId]);

    // Send analytics event when lottie starts playing (only once)
    // update frequency capping localStorage when lottie actually plays
    const hasSentAnalytics = useRef(false);
    const hasUpdatedFrequencyCap = useRef(false);
    useEffect(() => {
        if (showLottieOverlay && shouldPlayLottie) {
            // Update frequency capping in localStorage after lottie has played
            if (!hasUpdatedFrequencyCap.current) {
                shouldProceed({ mode: ProceedMode.CHECK_AND_UPDATE });
                hasUpdatedFrequencyCap.current = true;
            }

            if (onImpressionMagicItemsFtue && !hasSentAnalytics.current) {
                onImpressionMagicItemsFtue();
                hasSentAnalytics.current = true;
            }
        }
    }, [showLottieOverlay, shouldPlayLottie, onImpressionMagicItemsFtue, shouldProceed]);

    // Handlers for timer analytics events
    const handleTimerStart = useCallback(() => {
        if (onImpressionMagicItemsTimer) {
            // near_expiry is false when timer first starts
            onImpressionMagicItemsTimer(false);
        }
    }, [onImpressionMagicItemsTimer]);

    const handleUrgencyTimeReached = useCallback(() => {
        if (onImpressionMagicItemsTimer) {
            // near_expiry is true when timer time equals urgency time
            onImpressionMagicItemsTimer(true);
        }
    }, [onImpressionMagicItemsTimer]);

    // Determine if we should show only message + timer (hide items)
    const showOnlyMessageAndTimer = useMemo(() => {
        return (
            showMagicItemsUI &&
            !isMagicItemsExpired &&
            allCrossSellSuggestionsAdded &&
            !isEmpty(allCrossSellSuggestionsAddedText)
        );
    }, [showMagicItemsUI, isMagicItemsExpired, allCrossSellSuggestionsAdded, allCrossSellSuggestionsAddedText]);

    // Send analytics event when all magic items have been added
    useEffect(() => {
        if (showOnlyMessageAndTimer && onImpressionMagicItemsEmpty) {
            onImpressionMagicItemsEmpty(allCrossSellSuggestionsAddedText);
        }
    }, [showOnlyMessageAndTimer, onImpressionMagicItemsEmpty, allCrossSellSuggestionsAddedText]);

    return {
        // Magic items state
        isMagicItems,
        isWidgetUnlocked,
        isMagicItemsExpired,
        showMagicItemsUI,
        showOnlyMessageAndTimer,

        // Lottie overlay state
        showLottieOverlay,
        isLottieVisible,
        lottieUrl,
        handleMagicDealsLottieComplete,
        handleLottieDOMLoaded,
        shouldPlayLottie,

        // Timer handlers
        handleTimerComplete,
        handleTimerStart,
        handleUrgencyTimeReached,
    };
};
